import React, { Component, Fragment } from 'react';
import { Redirect, withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';
import qs from 'query-string';
import { connect } from 'react-redux';

import { AlbumByJoinCode } from '../../graphql/queries/albums';
import { areValidIdentities } from '../../util/account';
import { selectIdentityForPurchase as selectIdentityForPurchaseAction } from '../../actions/purchase-album';
import { sentryCaptureMessage } from '../../util/sentry';
import { AlbumMembershipWithAccountIdentitiesForRedirect } from '../../graphql/queries/album-membership';
import { getAlbumMembershipBranchLinkRedirectId } from '../../util/purchase-album';
import HeadOpenGraphMeta from '../../components/global/HeadOpenGraphMeta';
import { getThumbBySize } from '../../util/photo';
import { IMAGE_SIZE_CODE } from '../../constants/image-size-codes';

class PurchaseAlbumRedirect extends Component {
  static propTypes = {
    albumMembership: PropTypes.shape({
      album: PropTypes.shape({
        id: PropTypes.string,
      }),
      matched_photos_extended: PropTypes.shape({
        total: PropTypes.number,
      }),
      entitled_matched_photos: PropTypes.shape({
        total: PropTypes.number,
      }),
    }),
    albumMembershipLoading: PropTypes.bool,
    albumLoading: PropTypes.bool,
    location: PropTypes.shape({
      search: PropTypes.string,
    }).isRequired,
    identities: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      }),
    ),
    accountId: PropTypes.string,
    albumId: PropTypes.string,
    selectIdentityForPurchase: PropTypes.func,
  };

  setIdentities(identities) {
    const { albumMembership, selectIdentityForPurchase } = this.props;

    if (!albumMembership) {
      return;
    }

    identities.forEach(id => selectIdentityForPurchase({ id }));
  }

  redirectToError(errorMessage) {
    const { location } = this.props;

    sentryCaptureMessage(`Purchase Album Redirect :: ${errorMessage}`, {
      level: 'error',
      extra: { location },
    });

    const redirectToError = {
      pathname: '/error',
      state: {
        error: 'Link is no longer valid',
      },
    };

    return <Redirect to={redirectToError} />;
  }

  render() {
    const {
      albumMembership,
      albumLoading,
      albumMembershipLoading,
      location,
      identities,
      accountId,
      albumId,
    } = this.props;

    // The entire logic must be determined strictly on the client side.
    if (albumLoading || albumMembershipLoading) {
      return null;
    }

    const { identities: queryIdentities, joinCode } = qs.parse(location.search);

    if (!albumMembership && !joinCode) {
      return this.redirectToError('Invalid album membership');
    }

    if (!albumId && joinCode) {
      return this.redirectToError('Invalid Join Code');
    }

    const queryIdentityArray = queryIdentities && JSON.parse(queryIdentities);
    if (
      queryIdentityArray &&
      queryIdentityArray.length > 0 &&
      !areValidIdentities(queryIdentityArray, identities)
    ) {
      sentryCaptureMessage('Purchase Album Redirect :: Invalid identity', {
        level: 'error',
        extra: {
          accountId,
        },
      });

      const redirectToError = {
        pathname: '/error',
        state: {
          error: 'Link is no longer valid',
        },
      };

      return <Redirect to={redirectToError} />;
    }

    if (queryIdentityArray && queryIdentityArray.length > 0) {
      this.setIdentities(queryIdentityArray);
    }

    const redirectId =
      albumId || getAlbumMembershipBranchLinkRedirectId(albumMembership);

    if (albumMembership) {
      return (
        <Fragment>
          <HeadOpenGraphMeta
            title={albumMembership.album.root_album_folder?.name}
            imageUrl={
              getThumbBySize(
                albumMembership.matched_photos_extended.records?.[0]
                  ?.thumbnail_urls,
                IMAGE_SIZE_CODE.MEDIUM2X,
              )?.url
            }
          />
          <Redirect to={`/consumer/matches/${redirectId}`} />
        </Fragment>
      );
    }

    return <Redirect to={`/purchase/album/${redirectId}/start`} />;
  }
}

const withAlbumMembership = graphql(
  AlbumMembershipWithAccountIdentitiesForRedirect,
  {
    options: ({ location }) => ({
      variables: {
        albumMembershipId: qs.parse(location.search).albumMembership,
      },
    }),
    props: ({ data: { albumMembership, me, loading } }) => ({
      albumMembership,
      identities: me?.linked_identities?.records,
      accountId: me?.id,
      albumMembershipLoading: loading,
    }),
    skip: ({ location }) => !qs.parse(location.search).albumMembership,
  },
);

const withAlbum = graphql(AlbumByJoinCode, {
  options: ({ location }) => {
    const joinCode = qs.parse(location.search).joinCode || null;

    return {
      variables: {
        joinCode,
      },
      skip: !joinCode,
    };
  },
  props: ({ data: { albumByJoinCode, loading } }) => ({
    albumId: albumByJoinCode && albumByJoinCode.id,
    albumLoading: loading,
  }),
  skip: ({ location }) => {
    const joinCode = qs.parse(location.search).joinCode || null;
    return !joinCode;
  },
});

const mapDispatchToProps = {
  selectIdentityForPurchase: selectIdentityForPurchaseAction,
};

export { PurchaseAlbumRedirect };

export default compose(
  withRouter,
  withAlbumMembership,
  withAlbum,
  connect(null, mapDispatchToProps),
)(PurchaseAlbumRedirect);
